import { useLingui } from "@lingui/react";
import { DownloadIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  exportRowsCSV,
  exportRowsXLSX,
  type ExportColumn,
} from "@/utils/cockpit-export";

interface ExportButtonsProps<T> {
  rows: T[];
  columns: ExportColumn<T>[];
  /** Base filename (no extension); the helpers append .csv/.xlsx. */
  filename: string;
  /** Worksheet name for the XLSX export. */
  sheetName?: string;
  /**
   * Total rows on the server for the active query. When this is greater than
   * the number of rows actually held (a paginated table only ships the current
   * page), the export is a partial slice — we then rename the file and show a
   * caption so the file never dishonestly claims to hold the full set.
   */
  totalCount?: number;
  /** Current page (1-based) and total page count, when paginated. */
  pageInfo?: { page: number; pageCount: number };
  /**
   * For client-side tables that fetch at most `cappedAt` rows: when the fetch
   * hit the cap there may be more rows than exported, so the export is honest
   * about showing only the first `cappedAt`.
   */
  truncated?: boolean;
  /** The fetch cap that `truncated` refers to (used for the filename/caption). */
  cappedAt?: number;
}

/**
 * CSV + XLSX export buttons for any verification table. Disabled while there are
 * no rows so an empty export never produces a misleading blank file.
 *
 * When the rows are only the current page of a larger server set
 * (`pageInfo.pageCount > 1` or `rows.length < totalCount`), or only the first
 * `cappedAt` of a truncated client fetch, the export is honest about it: the
 * filename gains a marker (so a `debtors_all…` base no longer over-claims) and a
 * muted caption states which slice was exported.
 */
export function ExportButtons<T>({
  rows,
  columns,
  filename,
  sheetName,
  totalCount,
  pageInfo,
  truncated,
  cappedAt,
}: ExportButtonsProps<T>) {
  const { i18n } = useLingui();
  const disabled = rows.length === 0;

  const pageCount = pageInfo?.pageCount ?? 1;
  const page = pageInfo?.page ?? 1;
  const isPaged =
    pageCount > 1 || (totalCount != null && rows.length < totalCount);
  const isCapped = truncated === true;

  // Honest filename: a partial slice carries a marker so the `.csv`/`.xlsx` no
  // longer claims the full set.
  let effectiveFilename = filename;
  if (isPaged) {
    effectiveFilename = `${filename}_page${page}of${pageCount}`;
  } else if (isCapped && cappedAt != null) {
    effectiveFilename = `${filename}_first${cappedAt}`;
  }

  let caption: string | null = null;
  if (isPaged) {
    caption = i18n._({
      id: "analytics.export.currentPage",
      message: "Current page ({page} of {pageCount})",
      values: { page, pageCount },
    });
  } else if (isCapped && cappedAt != null) {
    caption = i18n._({
      id: "analytics.export.capped",
      message: "First {count} rows only",
      values: { count: cappedAt },
    });
  }

  return (
    <div className="flex items-center gap-2">
      {caption ? (
        <span
          className="text-xs text-muted-foreground"
          data-testid="export-partial-caption"
        >
          {caption}
        </span>
      ) : null}
      <Button
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => exportRowsCSV(rows, columns, effectiveFilename)}
      >
        <DownloadIcon className="mr-1 h-4 w-4" />
        {i18n._({ id: "analytics.export.csv", message: "CSV" })}
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => exportRowsXLSX(rows, columns, effectiveFilename, sheetName)}
      >
        <DownloadIcon className="mr-1 h-4 w-4" />
        {i18n._({ id: "analytics.export.xlsx", message: "Excel" })}
      </Button>
    </div>
  );
}
